import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture'
import { Tools } from '@babylonjs/core/Misc/tools'
import { CameraAngle, CameraDefaults } from './Cameras'
import { setFogEnabled } from './Effects/Fog'
import { setMovingLightEnabled } from './Effects/LightControls'
import ModelViewer, { Theme } from './ModelViewer'
import { Settings } from './Settings'

export const TitaniumBackground = '#FBFBFB'

export const PreciousBackground = '#161514'

export class ModelViewerSettings {
	private _theme: Theme = 'Titanium'
	private _environment: string
	private _environmentRotation: number
	private _backgroundColor?: string = TitaniumBackground
	private _cameraSelectors?: CameraAngle[] = [CameraAngle.Front, CameraAngle.Perspective, CameraAngle.Side, CameraAngle.Top]
	private _maxZoom: number = CameraDefaults.maxZoom

	private _fog?: boolean
	private _movingLights?: boolean

	#viewer: ModelViewer
	contextMenu: boolean = true
	realWorldScale: boolean = false

	public get theme(): Theme {
		return this._theme
	}
	public set theme(value: Theme) {
		this._theme = value
		if (this.theme == 'Titanium') {
			this.backgroundColor = TitaniumBackground
		} else if (this._theme == 'Precious') {
			this.backgroundColor = PreciousBackground
		}
		this.environment = this._theme
		// this.#viewer.pipeline.theme = this.theme
		this.#viewer.updateThumbnails()
	}

	public get environment(): string {
		return this._environment
	}
	public set environment(value: string) {
		this._environment = value
		this.#viewer.loadEnvironment(value)
	}

	public get environmentRotation(): number {
		return this._environmentRotation
	}
	public set environmentRotation(value: number) {
		this._environmentRotation = value
		;(this.#viewer.scene.environmentTexture as CubeTexture).rotationY = Tools.ToRadians(value)
	}

	public get backgroundColor(): string {
		return this._backgroundColor
	}
	public set backgroundColor(value: string) {
		// this.#viewer.scene.clearColor = Color4.FromHexString(value)
		this.#viewer.canvas.style.backgroundColor = value
		this.#viewer.updateBackgroundColor(value)
		this._backgroundColor = value
	}

	public get cameraSelectors(): CameraAngle[] {
		return this._cameraSelectors
	}
	public set cameraSelectors(value: CameraAngle[]) {
		this._cameraSelectors = value
		// console.log("set selectors")
		this.#viewer.updateThumbnails()
	}

	public transitions?: ('Frame' | 'Camera')[] = ['Camera'] // ["Frame", "Camera"]

	private _cameraControls?: ('Zoom' | 'Orbit-Horizontal' | 'Orbit-Vertical' | 'Pan')[] = ['Zoom', 'Orbit-Horizontal', 'Orbit-Vertical']
	public get cameraControls(): ('Zoom' | 'Orbit-Horizontal' | 'Orbit-Vertical' | 'Pan')[] {
		return this._cameraControls
	}

	public set cameraControls(value: ('Zoom' | 'Orbit-Horizontal' | 'Orbit-Vertical' | 'Pan')[]) {
		this._cameraControls = value

		if (value.includes('Zoom')) {
			this.#viewer.camera.upperRadiusLimit = CameraDefaults.minZoom
			this.#viewer.camera.lowerRadiusLimit = this.maxZoom
		} else {
			this.#viewer.camera.lowerRadiusLimit = this.#viewer.camera.upperRadiusLimit = this.#viewer.camera.radius
		}

		this.#viewer.camera.angularSensibilityX = value.includes('Orbit-Horizontal') ? CameraDefaults.rotationSensibility : Number.MAX_VALUE
		this.#viewer.camera.angularSensibilityY = value.includes('Orbit-Vertical') ? CameraDefaults.rotationSensibility : Number.MAX_VALUE

		this.#viewer.camera.panningSensibility = value.includes('Pan') ? CameraDefaults.panningSensibility : Number.MAX_VALUE
	}

	public get maxZoom(): number {
		return this._maxZoom
	}
	public set maxZoom(value: number) {
		const isMaxZoom = this.#viewer.camera.radius == this.#viewer.camera.lowerRadiusLimit
		this._maxZoom = value
		this.#viewer.camera.lowerRadiusLimit = value
		this.#viewer.camera.maxZoom = value
		if (isMaxZoom) this.#viewer.camera.radius = value
	}

	private _AO: boolean = true
	public get AO(): boolean {
		return this._AO
	}
	public set AO(value: boolean) {
		this._AO = value
		Settings.AO = value
	}

	private _depthSorting: boolean = true
	public get depthSorting(): boolean {
		return this._depthSorting
	}
	public set depthSorting(value: boolean) {
		this._depthSorting = value
		Settings.DepthSorting = value
	}

	private _rayindex: boolean = true
	public get rayIndex(): boolean {
		return this._rayindex
	}
	public set rayIndex(value: boolean) {
		this._rayindex = value
		Settings.RayIndex = value
	}

	private _LOD: number = 0
	public get LOD(): number {
		return this._LOD
	}
	public set LOD(value: number) {
		this._LOD = value
		Settings.LOD = value
	}

	allowErrors: boolean

	postProcessing: boolean

	maxFrameWidth = 190
	maxFrameHeight = this.maxFrameWidth * 9 / 16

	public taa = {
		enabled: false,
		halton: 16,
		samples: 4,
		factor: 0.13
	}

	public get fog(): boolean {
		return this._fog
	}

	public set fog(value: boolean) {
		this._fog = value
		setFogEnabled(this.#viewer, value)
	}

	public get movingLights(): boolean {
		return this._movingLights
	}
	public set movingLights(value: boolean) {
		this._movingLights = value
		setMovingLightEnabled(this.#viewer, value)
	}

	constructor(viewer: ModelViewer) {
		this.#viewer = viewer
	}
}
